import { useEffect, RefObject, useRef } from 'react';

interface UseSwipeNavigationProps {
  containerRef: RefObject<HTMLElement>;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  disabled?: boolean;
  threshold?: number; // Minimum swipe distance to trigger navigation
}

/**
 * A hook that enables swipe-based navigation on a container element.
 * Swipe left to go to the next item, swipe right to go to the previous item.
 *
 * @param containerRef - Reference to the container element
 * @param onSwipeLeft - Callback function to execute when swiped left
 * @param onSwipeRight - Callback function to execute when swiped right
 * @param disabled - Whether the swipe navigation is disabled
 * @param threshold - Minimum distance (in pixels) required to trigger a swipe action
 */
const useSwipeNavigation = ({
  containerRef,
  onSwipeLeft,
  onSwipeRight,
  disabled = false,
  threshold = 50, // Default threshold in pixels
}: UseSwipeNavigationProps) => {
  // Use refs to store the callback functions to prevent re-renders
  const onSwipeLeftRef = useRef(onSwipeLeft);
  const onSwipeRightRef = useRef(onSwipeRight);

  // Update refs when callbacks change
  useEffect(() => {
    onSwipeLeftRef.current = onSwipeLeft;
    onSwipeRightRef.current = onSwipeRight;
  }, [onSwipeLeft, onSwipeRight]);

  // This effect will only run when containerRef or disabled changes
  // NOT when the callback functions change
  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) return;

    let startX: number | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (startX === null) return;

      const endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;

      // Check if the swipe distance exceeds the threshold
      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          // Swiped left - use the current ref value
          onSwipeLeftRef.current();
        } else {
          // Swiped right - use the current ref value
          onSwipeRightRef.current();
        }
      }

      startX = null;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [containerRef, disabled, threshold]); // Removed callback dependencies
};

export default useSwipeNavigation;
